import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { homePageModuleQuery } from '@data/sanity/queries/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityHomePage } from '@data/sanity/queries/types/page'
import { getLocaleLanguage } from '@lib/language'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getHomePage } from '@lib/sanity/page'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type HomePageProps = BasePageProps & {
  page: SanityHomePage
  site: SanitySiteFragment
}

const HomePage = ({ draftMode, locale, page, site }: HomePageProps) => {
  return (
    <Layout page={page} site={site} draftMode={draftMode}>
      {!draftMode && <Modules />}
      {draftMode && (
        <PreviewModules
          query={homePageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<HomePageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const homePage = await getHomePage(sanityClient, locale)

  if (!homePage.page) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: homePage.page,
      site: homePage.site,
    },
    revalidate: 60,
  }
}

export default HomePage
